import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, CardContent, Input, Badge } from '@/components/ui';
import { StatusIndicator } from '@/components/domain';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { useAuthStore, useUIStore } from '@/stores';
import { apiClient } from '@/api/client';
import { cn } from '@/utils/cn';
import { useMotionSafe } from '@/utils/motion';

interface RoleOption {
  id: 'student' | 'teacher';
  label: string;
  description: string;
  mark: string;
}

const roleOptions: RoleOption[] = [
  {
    id: 'student',
    label: 'Student',
    description: 'Take exam with monitoring',
    mark: 'S'
  },
  {
    id: 'teacher',
    label: 'Teacher', 
    description: 'Monitor and manage exams',
    mark: 'T'
  }
];

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageEnter, staggerContainer, staggerItem, hoverLift } = useMotionSafe();
  const { login, checkSession } = useAuthStore();
  const { showNotification } = useUIStore();
  
  // Get role from URL query parameter
  const urlParams = new URLSearchParams(location.search);
  const initialRole = urlParams.get('role') as 'student' | 'teacher' | null;
  
  const [formData, setFormData] = useState({
    name: '',
    role: (initialRole && ['student', 'teacher'].includes(initialRole) ? initialRole : '') as 'student' | 'teacher' | ''
  });
  const [errors, setErrors] = useState({
    name: '',
    role: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monitoringReady, setMonitoringReady] = useState<boolean | null>(null);

  // Check existing session on mount only
  useEffect(() => {
    const validateSession = async () => {
      try {
        await checkSession();
        // Get the current auth state after checkSession completes
        const currentState = useAuthStore.getState();
        if (currentState.isAuthenticated) {
          // Redirect based on existing role
          const userRole = localStorage.getItem('user_role');
          if (userRole === 'student') {
            const consentGiven = localStorage.getItem('consent_given');
            navigate(consentGiven === 'true' ? '/student' : '/consent');
          } else {
            navigate('/instructor');
          }
        }
      } catch (error) {
        console.log('No valid session found');
      }
    };

    validateSession();
  }, []); // Remove isAuthenticated from dependencies to prevent infinite loop

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const health = await apiClient.healthCheck() as {
          monitoring_ready?: boolean;
          monitoring_mode?: string;
          eth_xgaze?: string;
          data?: { monitoring_ready?: boolean; eth_xgaze?: string; monitoring_mode?: string };
        };
        const ready =
          health.monitoring_ready === true ||
          health.data?.monitoring_ready === true ||
          health.monitoring_mode === 'webgazer' ||
          health.data?.monitoring_mode === 'webgazer';
        if (!cancelled) setMonitoringReady(ready);
      } catch {
        if (!cancelled) setMonitoringReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateForm = (): boolean => {
    const newErrors = { name: '', role: '' };
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your full name';
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
      isValid = false;
    }

    // Validate role
    if (!formData.role) {
      newErrors.role = 'Please select your role';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await login(formData.name.trim(), formData.role as 'student' | 'teacher');
      
      showNotification({
        type: 'success',
        title: 'Login Successful',
        message: `Welcome, ${formData.name}!`
      });

      // Redirect based on role
      if (formData.role === 'student') {
        navigate('/consent');
      } else {
        navigate('/instructor');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorMessage = 'Unable to connect to server. Please check your connection and try again.';
        } else if (error.message.includes('HTTP error')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }

      showNotification({
        type: 'error',
        title: 'Login Failed',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleSelect = (roleId: 'student' | 'teacher') => {
    setFormData(prev => ({ ...prev, role: roleId }));
    setErrors(prev => ({ ...prev, role: '' }));
  };

  const canSubmit = formData.name.trim().length >= 2 && formData.role && !isSubmitting;

  return (
    <motion.div
      className="min-h-screen bg-surface-canvas font-sans text-content-primary"
      {...pageEnter}
    >
      {/* Header */}
      <nav className="border-b border-subtle bg-surface-canvas">
        <div className="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <BrandMark frameClassName="size-9 sm:size-9" />
            <span className="font-display text-xl font-normal">QuizSecure</span>
            <Badge variant="info" size="sm" className="hidden sm:inline-flex">AI-powered</Badge>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ThemeToggle />
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto flex min-h-[calc(100svh-73px)] max-w-page items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <motion.div
          className="w-full max-w-lg"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
        <Card size="lg" className="w-full">
          <CardContent className="p-6 sm:p-8 lg:p-10">
            <motion.div variants={staggerItem} className="mb-8 text-center">
              <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-card border border-hairline bg-surface-nested text-content-primary">
                <BrandMark framed={false} className="size-8" />
              </div>
              <h1 className="font-display text-heading font-normal">Welcome to QuizSecure</h1>
              <p className="mx-auto mt-3 max-w-sm text-content-secondary">
                Enter your details to access the exam monitoring system.
              </p>
            </motion.div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div variants={staggerItem}>
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    setErrors(prev => ({ ...prev, name: '' }));
                  }}
                  error={errors.name}
                  autoComplete="name"
                  required
                />
              </motion.div>

              <motion.fieldset
                variants={staggerItem}
                aria-describedby={errors.role ? 'role-error' : undefined}
              >
                <legend className="mb-3 block text-sm font-medium text-content-secondary">
                  Select your role
                </legend>
                <div className="grid grid-cols-2 gap-4">
                  {roleOptions.map((option) => (
                    <motion.button
                      key={option.id}
                      type="button"
                      onClick={() => handleRoleSelect(option.id)}
                      aria-pressed={formData.role === option.id}
                      {...hoverLift}
                      className={cn(
                        'min-h-36 rounded-control border p-4 text-left font-ui transition-[background-color,border-color,box-shadow] duration-200',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-parchment',
                        formData.role === option.id
                          ? 'border-ink bg-surface-nested'
                          : 'border-hairline bg-surface-card hover:border-ink hover:bg-surface-nested'
                      )}
                    >
                      <span
                        className="mb-4 flex size-9 items-center justify-center rounded-control border border-hairline bg-surface-card font-display text-lg font-normal"
                        aria-hidden="true"
                      >
                        {option.mark}
                      </span>
                      <span className="block font-medium text-content-primary">{option.label}</span>
                      <span className="mt-1 block text-xs leading-5 text-content-muted">
                        {option.description}
                      </span>
                    </motion.button>
                  ))}
                </div>
                {errors.role && (
                  <p id="role-error" role="alert" className="mt-2 text-sm text-state-critical">
                    {errors.role}
                  </p>
                )}
              </motion.fieldset>

              <motion.div variants={staggerItem}>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                  disabled={!canSubmit}
                  className="w-full"
                >
                  {isSubmitting
                    ? 'Connecting...'
                    : formData.role
                      ? `Continue as ${roleOptions.find(r => r.id === formData.role)?.label}`
                      : 'Continue to QuizSecure'
                  }
                </Button>
              </motion.div>
            </form>

            <motion.div variants={staggerItem} className="mt-8 border-t border-subtle pt-6">
              <div className="flex flex-col items-center justify-center gap-3 text-sm sm:flex-row">
                <StatusIndicator
                  status={monitoringReady === false ? 'offline' : 'online'}
                  label={monitoringReady === false ? 'API Degraded' : 'System Online'}
                />
                <StatusIndicator
                  status={
                    monitoringReady === true
                      ? 'ready'
                      : monitoringReady === false
                        ? 'critical'
                        : 'offline'
                  }
                  label={
                    monitoringReady === true
                      ? 'On-device gaze ready'
                      : monitoringReady === false
                        ? 'API unavailable'
                        : 'Checking API…'
                  }
                />
              </div>
              {monitoringReady === false && (
                <p className="mt-3 text-center text-sm text-state-critical">
                  API is unreachable. Gaze runs in your browser after login; the exam API must be online.
                </p>
              )}
            </motion.div>
          </CardContent>
        </Card>
        </motion.div>
      </main>
    </motion.div>
  );
};